<template>
    <div class="flex flex-col h-screen overflow-hidden">
        <!-- Top Header with Profile and Notifications - Fixed at top -->
        <div class="flex-shrink-0 h-16 flex items-center justify-between p-4 border-b border-echoes-border-gray/40 bg-echoes-background-black/50 backdrop-blur-md relative z-10">
            <div class="flex-1 flex items-center">
                <router-link to="/explore" class="flex items-center gap-2">
                    <img src='@/assets/images/avatar/orb_avatar.png' class="w-8 h-8 cursor-pointer" alt="PortexAi Logo" loading="lazy">
                </router-link>
                <div class="text-echoes-primary text-lg font-semibold ml-2">Backoffice</div>
            </div>
            <div class="flex items-center gap-3">
                <NotificationsBell />
                <div class="dropdown relative">
                    <button ref="dropdownToggle" @click="(e) => toggleDropdown('profile', e)"
                        class="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-400 to-slate-600 hover:from-slate-300 hover:to-slate-500 transition-all duration-300 overflow-hidden"
                        type="button"
                        :aria-label="'Open user profile menu'"
                        aria-haspopup="true"
                        :aria-expanded="dropdownOpen">
                        <img v-if="profilePicture" :src="profilePicture" alt="Profile avatar"
                            class="rounded-full w-full h-full p-[1px] object-cover" width="40" height="40">
                        <div v-else class="w-full h-full p-2 text-gray-100 flex items-center justify-center">
                            <User />
                        </div>
                    </button>

                    <!-- Profile Dropdown menu -->
                    <div v-show="dropdownOpen" 
                        class="dropdown-menu absolute right-0 mt-2 z-[10000] w-48 rounded-md overflow-hidden bg-echoes-background-black border border-echoes-border-gray shadow-xl"
                        @click.stop>
                        <div>
                            <div class="relative">
                                <div class="py-6 bg-gradient-to-tr from-slate-400 to-slate-600"></div>
                                <div class="absolute px-4 -bottom-8 start-0">
                                    <div class="flex items-center gap-2">
                                        <img v-if="profilePicture" :src="profilePicture" alt=""
                                            class="rounded-full size-12" width="48" height="48">
                                        <div v-else
                                            class="p-2 w-[3rem] h-[3rem] text-echoes-background-gray rounded-full bg-echoes-background-black">
                                            <User />
                                        </div>
                                        <span class="font-semibold text-[15px] mt-3 truncate">@{{ userInfos?.username || '' }}</span>
                                    </div>
                                </div>
                            </div>

                            <ul class="py-2 mt-8 text-start divide-y divide-echoes-border-gray">
                                <li>
                                    <div class="mb-2">
                                        <router-link to="/creator-profile"
                                            class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                            <div class="flex-shrink-0">
                                                <i class="uil uil-plus text-[18px] text-echoes-primary"></i>
                                            </div>
                                            <div>
                                                <p class="text-sm font-medium transition-all duration-200">Data Studio</p>
                                            </div>
                                        </router-link>
                                    </div>
                                </li>

                                <li>
                                    <div class="mb-2">
                                        <p class="px-2 py-1.5 text-xs font-semibold text-white uppercase">Account</p>
                                        <router-link v-if="hasPermission('buy')" to="/creator-profile?tab=purchases"
                                            class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                            <i class="uil uil-shopping-cart text-[18px] text-echoes-primary"></i>
                                            <span class="text-sm font-medium">Purchases</span>
                                        </router-link>
                                        <router-link v-if="hasPermission('listings')"
                                            to="/creator-profile?tab=sales"
                                            class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                            <i class="uil uil-usd-circle text-[18px] text-echoes-primary"></i>
                                            <span class="text-sm font-medium">Sales</span>
                                        </router-link>
                                        <router-link to="/creator-profile-edit"
                                            class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 text-white/80 hover:text-white transition-all duration-200">
                                            <i class="uil uil-setting text-[18px] text-echoes-primary"></i>
                                            <span class="text-sm font-medium">Settings</span>
                                        </router-link>
                                    </div>
                                </li>

                                <li>
                                    <div class="pt-2">
                                        <router-link @click="logout" to="/login"
                                            class="flex items-center gap-2 py-2 px-3 text-red-500 hover:bg-red-600/20 transition-all duration-200">
                                            <i class="uil uil-sign-out-alt text-[18px]"></i>
                                            <span class="text-sm font-medium">Logout</span>
                                        </router-link>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content Area with Sidebar and Main Content -->
        <div class="flex flex-1 overflow-hidden">
            <AdminNavbar />
            <main class="flex-1 overflow-y-auto">
                <router-view />
            </main>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import AdminNavbar from '@/components/navbar/AdminNavbar.vue';
import NotificationsBell from '@/components/navbar/notification-bell.vue';
import { useStorage } from '@/composables/use-storage';
import { useIcons } from '@/composables/icons.js';
import { useRouter } from 'vue-router';
import {
    disconnectWallet
} from '@/plugins/wagmi/connect';
import axios from '@/plugins/axios';

const { userInfo, hasPermission, clearUserData, authToken } = useStorage();
const { User } = useIcons();

const userInfos = ref(userInfo.value);
const profilePicture = ref(userInfos?.value?.profile_pic);

const router = useRouter();

const dropdownOpen = ref(false);
const dropdownToggle = ref(null);

const toggleDropdown = (dropdownType, event) => {
    if (event) {
        event.stopPropagation();
    }
    if (dropdownType === 'profile') {
        dropdownOpen.value = !dropdownOpen.value;
    }
};

const handleClickOutside = (event) => {
    // Check if click is on the dropdown button
    if (dropdownToggle.value && dropdownToggle.value.contains(event.target)) {
        return;
    }
    
    // Check if click is inside the dropdown container (button or menu)
    const dropdownContainer = event.target.closest('.dropdown');
    if (dropdownContainer) {
        return;
    }
    
    // Close dropdown if clicking outside
    if (dropdownOpen.value) {
        dropdownOpen.value = false;
    }
};

const logout = async () => {
    try {
        clearUserData();
        disconnectWallet();
        const response = await axios.post('/api/logout', {
            headers: {
                'Authorization': `Bearer ${authToken}`
            }
        });

        if (response.status === 200) {
            router.push('/login');
        }
    } catch (error) {
        console.error("Error logging out:", error);
    }
};

onMounted(() => {
    // Use a small delay to ensure the dropdown is rendered before checking clicks
    setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
    }, 100);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>
